import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { DatePeriodSelector } from "@/components/ui/date-period-selector";
import { KpiPeriod } from "@/models/org-kpis";
import { cn } from "@/utils/shadcn";

export type PeriodPreset = "this_month" | "last_month" | "last_30_days" | "custom";

const PRESETS: { key: Exclude<PeriodPreset, "custom">; label: string }[] = [
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_30_days", label: "Last 30 days" }
];

const DAY_MS = 86_400_000;
const utc = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d));

/**
 * The period a preset covers, as the API expects it (ISO strings).
 *
 * In UTC, not the browser's time zone: usage is counted per UTC day, and a
 * local-time month boundary (21:00 UTC the day before, in Riyadh) would pull
 * the neighbouring day into the period.
 */
export const periodFor = (preset: Exclude<PeriodPreset, "custom">, now = new Date()): KpiPeriod => {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  switch (preset) {
    case "this_month":
      return { from: utc(y, m, 1).toISOString(), to: now.toISOString() };
    case "last_month":
      return {
        from: utc(y, m - 1, 1).toISOString(),
        to: new Date(utc(y, m, 1).getTime() - 1).toISOString()
      };
    default:
      return {
        from: new Date(utc(y, m, now.getUTCDate()).getTime() - 29 * DAY_MS).toISOString(),
        to: now.toISOString()
      };
  }
};

/** The UTC day with the same calendar date the person picked. */
const pickedDay = (date: Date) => utc(date.getFullYear(), date.getMonth(), date.getDate());

interface PeriodPickerProps {
  preset: PeriodPreset;
  period: KpiPeriod;
  onChange: (preset: PeriodPreset, period: KpiPeriod) => void;
  className?: string;
}

/** Quick periods plus a custom range, shared by the organization KPI pages. */
export function PeriodPicker({ preset, period, onChange, className }: PeriodPickerProps) {
  const handleCustom = useCallback(
    (start: Date | null, end: Date | null) => {
      if (start && end) {
        onChange("custom", {
          from: pickedDay(start).toISOString(),
          to: new Date(pickedDay(end).getTime() + DAY_MS - 1).toISOString()
        });
      }
    },
    [onChange]
  );

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {PRESETS.map((p) => (
        <Button
          key={p.key}
          size="sm"
          variant={preset === p.key ? "default" : "outline"}
          onClick={() => onChange(p.key, periodFor(p.key))}
        >
          {p.label}
        </Button>
      ))}
      <DatePeriodSelector
        className="w-auto"
        onDateChange={handleCustom}
        maxDate={new Date()}
        placeholder="Custom range"
        initialStartDate={preset === "custom" ? new Date(period.from) : undefined}
        initialEndDate={preset === "custom" ? new Date(period.to) : undefined}
      />
    </div>
  );
}
